import { useMutation, useQueryClient } from '@tanstack/react-query';
import { adminFetch } from '@/lib/apiClient';
import { throwFromResponse } from '../lib/apiErrorMessage';
import type { AdminMemberDetail, MemberType } from './useAdminUsersQuery';
import { userDetailQueryKey } from './useUserDetailQuery';
import { adminUserDetailQueryKey } from './useAdminUsersQuery';

/**
 * B2B(2026-06-20) D3 — 관리자 회원 유형 전환(개인 ↔ 기업).
 *
 * 계약(01-CONTRACT §5): `PATCH /api/admin/v1/users/{userId}/member-type`
 *   body `{ memberType, businessName?, businessRegistrationNo? }`.
 *   - PERSONAL → BUSINESS: 상호/사업자등록번호 필수, 즉시 ACTIVE(BE 처리).
 *   - BUSINESS → PERSONAL: 상호/번호/등급은 BE 에서 보존하되 미노출.
 *
 * ※ 조율 문서의 `/admin/v2/...` 는 논리 별칭일 뿐, 게이트웨이 정본 경로는 v1 이다(계약 §0).
 * 신규 엔드포인트는 generated OpenAPI 타입에 아직 없을 수 있어 raw fetch +
 * adminFetch(401 refresh 복구 포함)로 배선한다(users 훅 패턴). 게이트웨이 프록시
 * prefix(`/api`)를 붙여 호출한다.
 */

export interface MemberTypeChangeVars {
  userId: string;
  memberType: MemberType;
  /** BUSINESS 전환 시 필수. PERSONAL 전환 시 무시(전송하지 않음). */
  businessName?: string | undefined;
  businessRegistrationNo?: string | undefined;
}

interface MemberTypeChangeEnvelope {
  success?: boolean;
  data?: AdminMemberDetail | null;
}

export const useMemberTypeMutation = () => {
  const qc = useQueryClient();
  return useMutation<AdminMemberDetail | null, Error, MemberTypeChangeVars>({
    mutationFn: async ({ userId, memberType, businessName, businessRegistrationNo }) => {
      const body =
        memberType === 'BUSINESS'
          ? {
              memberType,
              businessName: businessName?.trim() ?? '',
              businessRegistrationNo: businessRegistrationNo?.trim() ?? '',
            }
          : { memberType };
      const res = await adminFetch(`/api/admin/v1/users/${userId}/member-type`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      });
      if (!res.ok) await throwFromResponse(res, '회원 유형 변경에 실패했습니다.');
      const env = (await res.json().catch(() => null)) as MemberTypeChangeEnvelope | null;
      return env?.data ?? null;
    },
    onSuccess: (_data, { userId }) => {
      // 상세(2종 키)·목록·사업자회원 목록 모두 무효화하여 즉시 반영.
      void qc.invalidateQueries({ queryKey: userDetailQueryKey(userId, true) });
      void qc.invalidateQueries({ queryKey: userDetailQueryKey(userId, false) });
      void qc.invalidateQueries({ queryKey: adminUserDetailQueryKey(userId) });
      void qc.invalidateQueries({ queryKey: ['admin', 'users'] });
    },
  });
};
